import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, Checkbox } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Risk {
  id: string;
  risk: string;
  trigger: string;
  countermeasure: string;
  owner: string;
  status: string;
}

const constraintTypes = [
  'Time', 'Budget', 'Compliance/Security', 'Tech dependencies', 
  'Data/Privacy', 'Seasonality', 'Staffing'
];

export default function ConstraintsRisksPage() {
  const [constraints, setConstraints] = useState<Record<string, boolean>>({});
  const [otherConstraint, setOtherConstraint] = useState('');
  const [risks, setRisks] = useState<Risk[]>([
    { id: '1', risk: '', trigger: '', countermeasure: '', owner: '', status: '' },
    { id: '2', risk: '', trigger: '', countermeasure: '', owner: '', status: '' },
    { id: '3', risk: '', trigger: '', countermeasure: '', owner: '', status: '' },
    { id: '4', risk: '', trigger: '', countermeasure: '', owner: '', status: '' },
    { id: '5', risk: '', trigger: '', countermeasure: '', owner: '', status: '' },
  ]);

  const updateRisk = (id: string, field: keyof Risk, value: string) => {
    setRisks(risks.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const toggleConstraint = (type: string) => {
    setConstraints({ ...constraints, [type]: !constraints[type] });
  };

  return (
    <TaskPage
      phase="engage"
      title="Constraints & Risks"
      pageNumber={4}
      instruction="See it before it stings. Identify constraints and risks that could derail the work."
      whyItMatters="Making constraints explicit reduces extraneous mental load and frees attention for the intrinsic problem."
      coachRail={{
        timing: "10-15 minutes",
        tips: "Be honest about constraints. It's better to surface them early.",
        debrief: "Which risks are most likely? Which constraints are hardest?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Constraints Checklist</h3>
          <div className="space-y-3">
            {constraintTypes.map(type => (
              <label key={type} className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={constraints[type] || false}
                  onChange={() => toggleConstraint(type)}
                  label=""
                />
                <span className="text-[var(--text-sec)]">{type}</span>
              </label>
            ))}
            <div className="flex gap-2 mt-4">
              <input
                type="text"
                value={otherConstraint}
                onChange={(e) => setOtherConstraint(e.target.value)}
                placeholder="Other constraint..."
                className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm"
              />
              {otherConstraint && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={constraints[otherConstraint] || false}
                    onChange={() => toggleConstraint(otherConstraint)}
                    label=""
                  />
                  <span className="text-[var(--text-sec)]">Add</span>
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6">
          <h3 className="text-lg font-semibold text-[var(--coral)] mb-4">Top 5 Risks</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--coral-dim)] border-b-2 border-[var(--coral)]">
                  <th className="p-2 text-left text-sm font-semibold text-[var(--coral)]">Risk</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--coral)]">Trigger to watch</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--coral)]">Countermeasure</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--coral)]">Owner</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--coral)]">Status</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((risk) => (
                  <tr key={risk.id} className="border-b border-[var(--coral)]">
                    <td className="p-2">
                      <TextInput
                        value={risk.risk}
                        onChange={(e) => updateRisk(risk.id, 'risk', e.target.value)}
                        placeholder="What's the risk?"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={risk.trigger}
                        onChange={(e) => updateRisk(risk.id, 'trigger', e.target.value)}
                        placeholder="What to watch for"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={risk.countermeasure}
                        onChange={(e) => updateRisk(risk.id, 'countermeasure', e.target.value)}
                        placeholder="First countermeasure"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={risk.owner}
                        onChange={(e) => updateRisk(risk.id, 'owner', e.target.value)}
                        placeholder="Owner"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <select
                        value={risk.status}
                        onChange={(e) => updateRisk(risk.id, 'status', e.target.value)}
                        className="w-full px-2 py-1 border border-[var(--rule)] rounded text-sm"
                      >
                        <option value="">Select</option>
                        <option value="R">R (Red)</option>
                        <option value="Y">Y (Yellow)</option>
                        <option value="G">G (Green)</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Callout type="tip" phase="engage">
          <p className="text-sm">Status: R = Red (high concern), Y = Yellow (monitor), G = Green (low concern)</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






